import React, { useState, useEffect, useMemo } from "react";
import { Plus, DoorOpen, Check, X as XIcon, Download, Pencil, Trash2 } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import {
  moveRequests as api,
  units as unitsApi,
  residents as residentsApi,
  type MoveRequest,
  type Unit,
  type Resident,
  type ApiError,
} from "../../services/api";
import DataGrid from "../../components/DataGrid";
import SearchSelect from "../../components/SearchSelect";
import { downloadBlob } from "../../utils/downloadBlob";

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};
const actionBtn = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "4px 6px",
  borderRadius: 6,
  color: "var(--nx-text-muted)",
  display: "inline-flex",
  alignItems: "center",
};

const FORM_INIT = { unitId: "", type: "MOVE_IN" as "MOVE_IN" | "MOVE_OUT", residentId: "", requestedDate: "", remarks: "" };
type FormState = typeof FORM_INIT;

const statusColor = (s: string) =>
  s === "APPROVED"
    ? { bg: "rgba(16,185,129,0.1)", text: "#10B981" }
    : s === "REJECTED"
      ? { bg: "rgba(239,68,68,0.1)", text: "#EF4444" }
      : { bg: "rgba(245,158,11,0.1)", text: "#D97706" };

const MoveRequestsPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  const isAdmin = user?.role === "SOCIETY_ADMIN" || user?.role === "SUPER_ADMIN";
  // Browsing the full unit directory is an admin/staff capability — GET /units (list) is
  // intentionally not resident/tenant-accessible on the backend (see SecurityConfig). A
  // resident only ever needs their own unit, which we already get for free from their own
  // resident record below, so we skip the forbidden call entirely for that role instead of
  // hitting a 401 on every page load.
  const canBrowseUnits = user?.role !== "RESIDENT" && user?.role !== "TENANT";

  const [unitList, setUnitList] = useState<Unit[]>([]);
  const [ownUnitId, setOwnUnitId] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [items, setItems] = useState<MoveRequest[]>([]);
  const [residentList, setResidentList] = useState<Resident[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState<FormState>(FORM_INIT);
  const [actingId, setActingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");

  const [editTarget, setEditTarget] = useState<MoveRequest | null>(null);
  const [editForm, setEditForm] = useState<FormState>(FORM_INIT);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<MoveRequest | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    if (!societyId) return;
    if (canBrowseUnits) {
      unitsApi.list(societyId, { size: 500 }).then(page => setUnitList(page.content)).catch(() => {});
    }
    residentsApi.list(societyId, { size: 500 }).then(page => setResidentList(page.content)).catch(() => {});
  }, [societyId, canBrowseUnits]);

  // Residents/tenants: `residentsApi.list` already scopes to just their own household
  // (see ResidentService.list), so their own unit comes along for free — no need to
  // ever call the units-list endpoint or show a "search all units" picker.
  useEffect(() => {
    if (canBrowseUnits || ownUnitId) return;
    const own = residentList.find(r => r.unitId)?.unitId;
    if (own) setOwnUnitId(own);
  }, [canBrowseUnits, residentList, ownUnitId]);

  const sortedUnitList = useMemo(
    () =>
      [...unitList].sort((a, b) => {
        const blockCmp = (a.blockName || "").localeCompare(b.blockName || "", undefined, {
          numeric: true,
          sensitivity: "base",
        });
        if (blockCmp !== 0) return blockCmp;
        return a.unitNumber.localeCompare(b.unitNumber, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }),
    [unitList],
  );

  const loadRequests = () => {
    if (!societyId) return;
    setLoading(true);
    const request = canBrowseUnits
      ? api.listAll(societyId)
      : ownUnitId
        ? api.list(societyId, ownUnitId)
        : Promise.resolve([]);
    request.then(setItems).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(loadRequests, [societyId, canBrowseUnits, ownUnitId]);

  const unitLabel = (id?: string) => {
    if (!id) return "—";
    const u = unitList.find(x => x.id === id);
    if (u) return u.blockName ? `${u.blockName} — ${u.unitNumber}` : u.unitNumber;
    // Residents/tenants never get unitList populated (they can't browse the full unit
    // directory), but their own resident record already carries their unit's
    // number/block — fall back to that instead of showing a dash for it.
    const r = residentList.find(x => x.unitId === id);
    if (r) return r.blockName ? `${r.blockName} — ${r.unitNumber}` : (r.unitNumber || "—");
    return "—";
  };
  const residentsForUnit = (unitId: string) => residentList.filter(r => r.unitId === unitId);
  const residentName = (id: string) => residentList.find(r => r.id === id)?.fullName || id;

  const visibleItems = unitFilter ? items.filter(r => r.unitId === unitFilter) : items;

  const openAdd = () => { setForm({ ...FORM_INIT, unitId: canBrowseUnits ? "" : ownUnitId }); setFormError(""); setShowForm(true); };
  const closeForm = () => { setShowForm(false); setFormError(""); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !form.unitId) { setFormError("Please select a unit."); return; }
    if (!form.residentId) { setFormError("Please select a resident."); return; }
    setSaving(true); setFormError("");
    try {
      const created = await api.create(societyId, form.unitId, {
        type: form.type,
        residentId: form.residentId,
        requestedDate: form.requestedDate || undefined,
        remarks: form.remarks || undefined,
      });
      setItems(p => [created, ...p]);
      closeForm();
    } catch (err) {
      setFormError((err as ApiError).message || "Failed to submit request. Please try again.");
    } finally { setSaving(false); }
  };

  const openEdit = (r: MoveRequest) => {
    setEditTarget(r);
    setEditForm({ unitId: r.unitId, type: r.type, residentId: r.residentId, requestedDate: r.requestedDate || "", remarks: r.remarks || "" });
    setEditError("");
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !editTarget) return;
    setEditSaving(true); setEditError("");
    try {
      const updated = await api.update(societyId, editTarget.id, {
        type: editForm.type,
        residentId: editForm.residentId || undefined,
        requestedDate: editForm.requestedDate || undefined,
        remarks: editForm.remarks || undefined,
      });
      setItems(p => p.map(x => x.id === updated.id ? updated : x));
      setEditTarget(null);
    } catch (err) {
      setEditError((err as ApiError).message || "Failed to update request.");
    } finally { setEditSaving(false); }
  };

  const confirmDelete = async () => {
    if (!societyId || !deleteTarget) return;
    setDeleting(true); setDeleteError("");
    try {
      await api.delete(societyId, deleteTarget.id);
      setItems(p => p.filter(x => x.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError((err as ApiError).message || "Failed to delete request.");
    } finally { setDeleting(false); }
  };

  const handleApprove = async (r: MoveRequest) => {
    if (!societyId) return;
    setActingId(r.id); setActionError("");
    try {
      const updated = await api.approve(societyId, r.id);
      setItems(p => p.map(x => x.id === updated.id ? updated : x));
    } catch (err) {
      setActionError((err as ApiError).message || "Failed to approve request.");
    } finally { setActingId(null); }
  };

  const handleReject = async (r: MoveRequest) => {
    if (!societyId) return;
    setActingId(r.id); setActionError("");
    try {
      const updated = await api.reject(societyId, r.id);
      setItems(p => p.map(x => x.id === updated.id ? updated : x));
    } catch (err) {
      setActionError((err as ApiError).message || "Failed to reject request.");
    } finally { setActingId(null); }
  };

  const handleDownloadNoc = async (r: MoveRequest) => {
    if (!societyId) return;
    try {
      const blob = await api.downloadNoc(societyId, r.unitId, r.id);
      downloadBlob(blob, `noc-${r.id}.pdf`);
    } catch {
      setActionError("Failed to download NOC. Please try again.");
    }
  };

  const canShowForm = canBrowseUnits || !!ownUnitId;

  const fields = (f: FormState, setF: React.Dispatch<React.SetStateAction<FormState>>) => (
    <>
      <div>
        <label style={lbl}>Unit *</label>
        {canBrowseUnits ? (
          <SearchSelect
            value={f.unitId}
            onChange={v => setF(p => ({ ...p, unitId: v, residentId: "" }))}
            options={sortedUnitList.map(u => ({ value: u.id, label: u.blockName ? `${u.blockName} — ${u.unitNumber}` : u.unitNumber }))}
            placeholder="Search units…"
            required
          />
        ) : (
          <div style={{ ...inp, color: f.unitId ? "var(--nx-text-primary)" : "var(--nx-text-muted)" }}>
            {f.unitId ? unitLabel(f.unitId) : "Loading your unit…"}
          </div>
        )}
      </div>
      <div>
        <label style={lbl}>Request Type *</label>
        <select value={f.type} onChange={e => setF(p => ({ ...p, type: e.target.value as FormState["type"] }))} style={inp}>
          <option value="MOVE_IN">Move In</option>
          <option value="MOVE_OUT">Move Out</option>
        </select>
      </div>
      <div>
        <label style={lbl}>Resident *</label>
        <SearchSelect
          value={f.residentId}
          onChange={v => setF(p => ({ ...p, residentId: v }))}
          options={residentsForUnit(f.unitId).map(r => ({ value: r.id, label: r.fullName }))}
          placeholder={!f.unitId ? "Select a unit first" : residentsForUnit(f.unitId).length === 0 ? "No residents on this unit" : "Search residents…"}
          required
          disabled={!f.unitId || residentsForUnit(f.unitId).length === 0}
        />
      </div>
      <div>
        <label style={lbl}>Requested Date</label>
        <input type="date" value={f.requestedDate} onChange={e => setF(p => ({ ...p, requestedDate: e.target.value }))} style={inp} />
      </div>
      <div>
        <label style={lbl}>Remarks</label>
        <textarea value={f.remarks} onChange={e => setF(p => ({ ...p, remarks: e.target.value }))} placeholder="Optional notes" style={{ ...inp, minHeight: 70, resize: "vertical" }} />
      </div>
    </>
  );

  return (
    <PageLayout>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 22, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 4 }}>
            Move Requests
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            Move-in / move-out workflow and NOC issuance
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {canBrowseUnits && (
            <div style={{ minWidth: 220 }}>
              <SearchSelect
                value={unitFilter}
                onChange={setUnitFilter}
                options={sortedUnitList.map(u => ({ value: u.id, label: u.blockName ? `${u.blockName} — ${u.unitNumber}` : u.unitNumber }))}
                placeholder="Filter by unit…"
              />
            </div>
          )}
          {canShowForm && (
            <button onClick={openAdd} className="nx-btn nx-btn-primary" style={{ gap: 6 }}>
              <Plus size={14} /> New Request
            </button>
          )}
        </div>
      </div>

      {actionError && (
        <div style={{ padding: "10px 14px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 10, fontSize: 12, color: "#EF4444", marginBottom: 16 }}>
          {actionError}
        </div>
      )}

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataGrid
          columns={[
            {
              key: "unit", header: "Unit",
              cellStyle: { color: "var(--nx-text-secondary)" },
              render: (r: MoveRequest) => unitLabel(r.unitId),
            },
            {
              key: "type", header: "Type",
              render: (r: MoveRequest) => r.type === "MOVE_IN" ? "Move In" : "Move Out",
            },
            {
              key: "resident", header: "Resident",
              cellStyle: { color: "var(--nx-text-secondary)" },
              render: (r: MoveRequest) => residentName(r.residentId),
            },
            {
              key: "requestedDate", header: "Requested Date",
              cellStyle: { color: "var(--nx-text-muted)" },
              render: (r: MoveRequest) => r.requestedDate || "—",
            },
            {
              key: "status", header: "Status",
              render: (r: MoveRequest) => {
                const sc = statusColor(r.status);
                return (
                  <span style={{ padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>
                    {r.status}
                  </span>
                );
              },
            },
            {
              key: "actions", header: "",
              cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
              render: (r: MoveRequest) => (
                <>
                  {isAdmin && r.status === "PENDING" && (
                    <>
                      <button onClick={() => handleApprove(r)} disabled={actingId === r.id} style={{ ...actionBtn, color: "#10B981" }} title="Approve">
                        <Check size={14} />
                      </button>
                      <button onClick={() => handleReject(r)} disabled={actingId === r.id} style={{ ...actionBtn, color: "#EF4444" }} title="Reject">
                        <XIcon size={14} />
                      </button>
                      <button onClick={() => openEdit(r)} style={actionBtn} title="Edit request">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => { setDeleteTarget(r); setDeleteError(""); }} style={{ ...actionBtn, color: "#EF4444" }} title="Delete request">
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                  {r.type === "MOVE_OUT" && r.status === "APPROVED" && (
                    <button onClick={() => handleDownloadNoc(r)} style={actionBtn} title="Download NOC">
                      <Download size={14} />
                    </button>
                  )}
                </>
              ),
            },
          ]}
          rows={visibleItems}
          rowKey={r => r.id}
          loading={loading}
          emptyIcon={<DoorOpen size={32} style={{ color: "var(--nx-text-muted)" }} />}
          emptyText="No move requests yet."
        />
      </div>

      {showForm && (
        <Modal
          title="New Move Request"
          submitLabel="Submit Request"
          error={formError}
          isLoading={saving}
          onClose={closeForm}
          onSubmit={handleSubmit}
        >
          {fields(form, setForm)}
        </Modal>
      )}

      {editTarget && (
        <Modal
          title="Edit Move Request"
          submitLabel="Save Changes"
          error={editError}
          isLoading={editSaving}
          onClose={() => setEditTarget(null)}
          onSubmit={handleEditSubmit}
        >
          {fields(editForm, setEditForm)}
        </Modal>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        variant="danger"
        title="Delete move request?"
        message={`Remove this ${deleteTarget?.type === "MOVE_IN" ? "move-in" : "move-out"} request for ${deleteTarget ? residentName(deleteTarget.residentId) : ""}? This action cannot be undone.`}
        confirmLabel="Delete Request"
        isLoading={deleting}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => { setDeleteTarget(null); setDeleteError(""); }}
      />
    </PageLayout>
  );
};

export default MoveRequestsPage;
